import { Card, Flex, SegmentedControl } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonTooltip } from 'components/common/tooltip';
import { PresetTrainingContext } from 'components/machine/dialogs/preset-training/context';
import { PresetForm } from 'components/machine/dialogs/preset-training/controls/preset-form';
import { AimingContext } from 'contexts/aiming.context';
import { MachineContext } from 'contexts/machine.context';
import { t } from 'i18next';
import { BuildPriority } from 'lib_ts/enums/pitches.enums';
import {
  PresetTrainingMode,
  TrackingDevice,
} from 'lib_ts/enums/training.enums';
import { useCallback, useContext, useEffect } from 'react';
import { useAuthStore } from 'stores/use-auth-store';

const tPresetMode = (mode: PresetTrainingMode) => {
  switch (mode) {
    case PresetTrainingMode.Balanced: {
      return t('tr.preset-balanced');
    }
    case PresetTrainingMode.Custom: {
      return t('tr.preset-custom');
    }
    case PresetTrainingMode.Precision: {
      return t('tr.preset-precision');
    }
    case PresetTrainingMode.Quick: {
      return t('tr.preset-quick');
    }
    default: {
      return mode;
    }
  }
};

export const PresetSelector = () => {
  const { active, disableForms, options, changeMode } = useContext(
    PresetTrainingContext
  );

  const { machine } = useContext(MachineContext);

  const { pitchTraining } = useContext(AimingContext);

  // assumption - non-admins cannot open training dialog for spin prio pitches while on Vision
  const onVision = machine.tracking_device === TrackingDevice.TrajektVision;
  const spinPrioPitch = pitchTraining?.priority === BuildPriority.Spins;

  const qtOnly = onVision && spinPrioPitch;

  useEffect(() => {
    if (!qtOnly) {
      return;
    }

    changeMode(PresetTrainingMode.Quick);
  }, [qtOnly]);

  const selectMode = useCallback(
    (mode: PresetTrainingMode) => {
      if (disableForms) {
        console.warn('Cannot change PT mode while forms are disabled');
        return;
      }

      if (mode === active.mode) {
        // already active
        return;
      }

      const preset = options.find((p) => p.mode === mode);

      if (!preset) {
        console.warn(`Cannot change PT mode to invalid preset ${mode}`);
        return;
      }

      // update the selected preset in the UI immediately
      changeMode(preset.mode);

      // save the selected spec to the user record in the DB (async)
      useAuthStore
        .getState()
        .updateUser({ preset_training_mode: preset.mode }, true);
    },
    [active.mode, disableForms]
  );

  const newTooltipContent = (
    <Card>
      <Flex direction="column" gap="2">
        <SegmentedControl.Root
          style={{ width: '100%' }}
          value={active.mode}
          onValueChange={(v) => {
            if (qtOnly && v !== PresetTrainingMode.Quick) {
              NotifyHelper.warning({
                message_md:
                  'Only Quick Train is available while using Trajekt Vision with spin priority pitches.',
              });
              return;
            }
            selectMode(v as PresetTrainingMode);
          }}
        >
          {options.map((o, i) => (
            <SegmentedControl.Item
              key={`mode-${i}`}
              value={o.mode}
              style={{
                // less padding => doesn't take up excess space on small screens
                paddingLeft: '4px',
                paddingRight: '4px',
              }}
            >
              {tPresetMode(o.mode)}
            </SegmentedControl.Item>
          ))}
        </SegmentedControl.Root>

        <PresetForm
          disabled={active.mode !== PresetTrainingMode.Custom || disableForms}
          preset={active}
        />
      </Flex>
    </Card>
  );

  return (
    <CommonTooltip
      text={t('tr.training-mode-change-msg').toString()}
      // only show tooltip while waiting on results and form is disabled
      disabled={!disableForms}
      trigger={newTooltipContent}
      side="right"
    />
  );
};
